// @ts-check

import { isNil } from "lodash-es";
import { useCallback, useEffect, useState } from "react";
import { export_form_preview, get_form } from '~/api/zero-api';
import { generateFormPages } from '~/other/forms';
import { Alert } from "antd";
import FormFieldsRenderer from '~/components/Forms/FormFieldsRenderer';
import LoadingIndicator from '~/components/Shared/LoadingIndicator';
import { PageSelectDropdown } from '~/components/Forms/PageSelectDropdown';
import { dateFormatterWithTime } from '~/other/Helper';
import Button from '~/components/Shared/Button';
import NotificationAlert from '~/other/NotificationAlert';

/**
 * @param {{
 *  formId?: string,
 *  fields?: any[],
 *  title?: string,
 *  printPreview?: boolean
 * }} props
 */
export default function FormPreview({ formId, fields: initialFields, title: initialTitle, printPreview = false }) {
    const hasFields = !isNil(initialFields);
    const [fields, setFields] = useState(initialFields);
    const [pages, setPages] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [title, setTitle] = useState(initialTitle);
    const [loading, setLoading] = useState(true);
    const [exportingPdf, setExportingPdf] = useState(false);

    const exportPdf = useCallback(async () => {
        try {
            setExportingPdf(true);
            const res = await export_form_preview(formId);
            const {data} = await res.json();
            if (data?.public_url) {
                window.location.href = data.public_url;
            } else {
                console.log(data);
            }
        } catch (error) {
            NotificationAlert('error', '', "Unable to export PDF preview.");
        } finally {
            setExportingPdf(false);
        }
    }, [formId]);

    const initializeFormFields = useCallback((fields) => {
        const pages = generateFormPages(fields, {ignorePageBreaks: printPreview});
        setFields(fields);
        setPages(pages);
        setLoading(false);
    }, [printPreview]);

    useEffect(() => {
        if (hasFields) {
            initializeFormFields(initialFields);
        } else {
            (async function () {
                setLoading(true);
                try {
                    const res = await get_form(formId);
                    const {form} = await res.json();
                    setTitle(form.name);
                    initializeFormFields(form.fields);
                } catch (error) {
                    console.error("Could not load form:", error);
                    setLoading(false);
                }
            })();
        }
    }, [formId, hasFields, initializeFormFields]);

    return (
        <>
            {!printPreview && (
                <div className="mar-btm-10">
                    <Alert
                        message="Here's what people will see when they are submitting this form. No responses are collected in this preview."
                        type="warning"
                    />
                    { formId && (
                        <div className="flex" style={{justifyContent: 'flex-end'}}>
                            <Button type="primary" className="mar-y-10" onClick={exportPdf} loading={exportingPdf}>Download PDF</Button>
                        </div>
                    )}
                </div>
            )}
            <div className="panel thin-border" style={{margin: '0 auto', display: 'flex', alignItems: 'center'}}>
                <div className="nav-header-panel" style={{
                    width: "87%",
                    borderRight: "1px solid #e2e2e2",
                    borderRadius: "0px",
                    display: "inline-block"
                }}>
                    <div className="team-name-header">
                        <h3 className={"section-titles admin-page-header"}
                            style={{display: "inline-block", margin: "0px"}}>
                            {title}
                        </h3>
                    </div>

                    <p className="header" style={{color: "#505050", marginBottom: "0px", paddingLeft: "2px"}}>
                        Submitting to Example Team. Last modified by Example User
                        on {dateFormatterWithTime(Date.now() / 1000)}.
                    </p>

                </div>
                <div style={{width: "13%", minWidth: "90px", display: "inline-block"}}>
                    <p className="zero-blue text-center" style={{margin: '0 auto'}}>
                        <b>Score</b>
                        <br/>
                        --
                    </p>
                </div>
            </div>
            <br/>
            <div className="panel pad-all thin-border">
                <PageSelectDropdown
                    pageCount={pages?.length ?? 0}
                    currentPage={(currentPage ?? 0) + 1}
                    onPageChange={(newPage) => {
                        setCurrentPage(newPage);
                    }}
                />
                { loading ? (
                    <LoadingIndicator />
                ) : (
                    <FormFieldsRenderer
                        form_uuid={formId}
                        submission_uuid=""
                        form_fields={fields}
                        pages={pages}
                        page={currentPage}
                        field_answers={[]}
                        toolbar_items={[]}
                        is_preview={true}
                    />
                )
                }
            </div>
            <div className="panel clear mar-btm-10" style={{textAlign: "right"}}>
                {
                    currentPage > 0 &&
                    <button className="btn btn-discard" onClick={() => {
                        setCurrentPage(currentPage - 1);
                    }} style={{marginRight: "8px"}}>
                        Previous Page
                    </button>
                }
                {
                    currentPage < pages.length - 1 &&
                    <button className="btn btn-primary" onClick={() => {
                        setCurrentPage(currentPage + 1);
                    }}>
                        Next Page
                    </button>
                }
            </div>
        </>
    );
}

